import Box from 'UIKit/Box/Box'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import Text from 'UIKit/Text'
import { Token } from 'config/types'

import Column from 'layout/Components/Column'
import { RowBetween, RowCenter } from 'layout/Components/Row'
import { Trans, useTranslation } from 'react-i18next'

import { FlexProps } from 'UIKit/Box/types'
import BigNumber from 'bignumber.js'
import { DepositFiatPaymentMethod } from 'config/types/payment'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import { useMemo } from 'react'
import { BankFormSelect } from 'views/Payment/DesktopPayment/components/BankSelectDropdown'
import ExtraInfo, { ExtraInfoLabel } from 'views/Payment/components/ExtraInfo'
import SelectedBonusItem from '../../components/SelectedBonusItem'
import {
  AmountDisplay,
  PaymentFormControl,
  PaymentFormInputAmount,
  PaymentStroke,
  StickyButtonWrapper,
  StyledActionButton,
} from '../../components/styles'
import { DepositFiatControls } from '../../hooks/useDeposit'
import { IBonusControl } from '../../types'

import FiatPaymentMethodModal, { FiatPaymentMethodModalProps } from './FiatPaymentMethodModal'

interface DepositFiatFormProps {
  selectedToken: Token
  bonusControl: IBonusControl
  depositControl: DepositFiatControls
}

const DepositFiatForm: React.FC<DepositFiatFormProps & FlexProps> = ({
  selectedToken,
  bonusControl,
  depositControl,
  ...props
}) => {
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()

  const [onPresentPaymentMethods] =
    useModal<FiatPaymentMethodModalProps<DepositFiatPaymentMethod>>(FiatPaymentMethodModal)

  const {
    handler: { onSelectMethod, onDeposit },
    status: { isFetchingFiatMethods, isFetchingMerchantSiteUrl },
    data: {
      selectedPaymentMethod,
      fiatMinMaxDeposit,
      paymentMethods,
      mobileRefForm: refForm,
      enableFormSubmit,
      returnedForm,
    },
  } = depositControl

  const { states, controls, isValid, errorsMessage } = useMemo(() => returnedForm, [returnedForm])

  return (
    <Column gap="var(--pm-space-component, 20px)" {...props}>
      {enableFormSubmit && <Box ref={refForm} />}

      {isMobile && (
        <BankFormSelect
          value={selectedPaymentMethod}
          onPresentMethodSelect={() => {
            onPresentPaymentMethods({
              methods: paymentMethods,
              onSelectMethod,
              selectedMethod: selectedPaymentMethod,
            })
          }}
        />
      )}

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={states.amount}
          label={t('Amount')}
          formatErrorMessage={() => errorsMessage.amount}
          disabled={isFetchingFiatMethods || !selectedToken || !selectedPaymentMethod}
        >
          <PaymentFormInputAmount
            tabIndex={1}
            control={controls.amount}
            name="amount"
            disabled={isFetchingFiatMethods || !selectedToken || !selectedPaymentMethod}
            value={states.amount.value}
            token={selectedToken}
            errors={!isFetchingFiatMethods && states.amount.errors}
            validators={controls.amount.validators}
            onErrorChanged={controls.amount.onErrorChanged}
            onValueChanged={controls.amount.onValueChanged}
            hideMax
            isInteger
            validateOnchange
          />
        </PaymentFormControl>

        {fiatMinMaxDeposit && (
          <Column gap="4px">
            <RowBetween>
              <AmountDisplay
                title="Min Deposit"
                amount={new BigNumber(fiatMinMaxDeposit.min)}
                currency={selectedToken}
              />
              <AmountDisplay title="No Fee" />
            </RowBetween>

            <AmountDisplay title="Max Deposit" amount={new BigNumber(fiatMinMaxDeposit.max)} currency={selectedToken} />
          </Column>
        )}
      </Column>
      {bonusControl.bonuses.length > 0 && (
        <>
          <PaymentStroke />
          <SelectedBonusItem
            inputAmount={states.amount.value}
            bonusControl={bonusControl}
            selectedToken={selectedToken}
          />
        </>
      )}

      <PaymentStroke />

      <ExtraInfo>
        <ExtraInfoLabel>
          <Trans>
            You will be redirected to a third-party site verified by HunnyPlay for a secure and trustworthy browsing
            experience
          </Trans>
        </ExtraInfoLabel>
      </ExtraInfo>

      <StickyButtonWrapper>
        <StyledActionButton
          onClick={() => onDeposit(Number(states.amount.value))}
          disabled={!isValid || !states.amount.value || !selectedPaymentMethod || isFetchingMerchantSiteUrl}
        >
          <RowCenter>
            {isFetchingMerchantSiteUrl && (
              <Box mr="12px">
                <CircleLoader />
              </Box>
            )}
            <Text>
              {isFetchingMerchantSiteUrl && <Trans>Redirecting</Trans>}
              {!isFetchingMerchantSiteUrl && (
                <>
                  {selectedPaymentMethod && <Trans>Deposit via {{ methodName: selectedPaymentMethod.name }}</Trans>}
                  {!selectedPaymentMethod && <Trans>Deposit</Trans>}
                </>
              )}
            </Text>
          </RowCenter>
        </StyledActionButton>
      </StickyButtonWrapper>
    </Column>
  )
}

export default DepositFiatForm
